import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenuAlt3, HiX, HiChevronRight, HiHome, HiUserGroup, HiLightningBolt, HiRefresh, HiOfficeBuilding, HiBriefcase, HiPhone, HiPhotograph } from 'react-icons/hi';
import { FaWhatsapp, FaCamera } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/imges/SKY MUSE LOGO (2).png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const location = useLocation();

  // WhatsApp configuration
  const whatsappNumber = '918700186979';
  const whatsappMessage = 'Hello! I would like to get started with your digital marketing services.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navLinks = [
    { name: 'Home', href: '/', icon: HiHome },
    { name: 'About', href: '/about', icon: HiUserGroup },
    { name: 'Services', href: '/services', icon: HiLightningBolt },
    { name: 'Photo & Video Shoot', href: '/media-shoot', icon: FaCamera },
    { name: 'Process', href: '/process', icon: HiRefresh },
    { name: 'Industries', href: '/industries', icon: HiOfficeBuilding },
    { name: 'Portfolio', href: '/portfolio', icon: HiBriefcase },
    { name: 'Contact', href: '/contact', icon: HiPhone },
  ];

  // Check if link is active
  const isActiveLink = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // Navbar link animation variants
  const linkVariants = {
    initial: { opacity: 0, y: -10 },
    animate: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.3 }
    }),
    hover: {
      y: -3,
      transition: { duration: 0.2 }
    }
  };

  // Mobile menu animation variants
  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      x: '100%',
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        staggerChildren: 0.07,
        delayChildren: 0.2
      }
    }
  };

  const mobileItemVariants = {
    closed: { x: 50, opacity: 0 },
    open: { x: 0, opacity: 1 }
  };

  return (
    <>
      {/* Main Navbar */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className={`fixed w-full z-50 transition-all duration-500 ${
          scrolled 
            ? 'top-0 bg-white/95 backdrop-blur-xl shadow-2xl shadow-black/10 py-2' 
            : 'top-0 bg-white/80 backdrop-blur-lg py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center relative z-10"
            >
              <Link to="/" className="flex items-center group">
                <motion.div
                  className="relative"
                  whileHover={{ rotate: [0, -5, 5, -5, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  <motion.img 
                    src={logo} 
                    alt="Sky Muse Logo" 
                    className="w-14 h-14 md:w-16 md:h-16 object-contain"
                  />
                  {/* Logo glow effect */}
                  <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </motion.div>
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center">
              <div className="flex items-center bg-gray-100/80 rounded-full px-2 py-1.5 backdrop-blur-sm">
                {navLinks.map((link, index) => {
                  const isActive = isActiveLink(link.href);
                  const isHash = link.href.startsWith('/#');
                  const LinkComponent = isHash ? 'a' : Link;
                  const linkProps = isHash ? { href: link.href } : { to: link.href };

                  return (
                    <motion.div 
                      key={link.name}
                      custom={index}
                      variants={linkVariants}
                      initial="initial"
                      animate="animate"
                      whileHover="hover"
                      onHoverStart={() => setHoveredLink(link.name)}
                      onHoverEnd={() => setHoveredLink(null)}
                      className="relative"
                    >
                      <LinkComponent
                        {...linkProps}
                        className={`relative px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 block ${
                          isActive 
                            ? 'text-white' 
                            : 'text-gray-700 hover:text-gray-900'
                        }`}
                      >
                        {/* Active/Hover Background */}
                        {isActive && (
                          <motion.div
                            layoutId="activeTab"
                            className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-500 rounded-full shadow-lg shadow-red-500/30"
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          />
                        )}
                        
                        {/* Hover Background */}
                        <AnimatePresence>
                          {hoveredLink === link.name && !isActive && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              className="absolute inset-0 bg-gray-200/80 rounded-full"
                            />
                          )}
                        </AnimatePresence>
                        
                        <span className="relative z-10 flex items-center gap-1.5">
                          {link.name}
                          {/* Active indicator dot */}
                          {isActive && (
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-1.5 h-1.5 bg-white rounded-full"
                            />
                          )}
                        </span>
                      </LinkComponent>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* CTA Button - Desktop */}
            <motion.button
              onClick={openWhatsApp}
              whileHover={{ 
                scale: 1.05, 
                boxShadow: '0 20px 40px rgba(220, 38, 38, 0.4)'
              }}
              whileTap={{ scale: 0.95 }}
              className="hidden lg:flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 via-red-500 to-red-600 bg-size-200 bg-pos-0 hover:bg-pos-100 text-white font-bold rounded-full shadow-lg shadow-red-500/30 hover:shadow-red-500/50 transition-all duration-500 cursor-pointer group"
              style={{ backgroundSize: '200% 100%' }}
            >
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
              >
                <FaWhatsapp className="text-lg" />
              </motion.div>
              <span>Get Started</span>
              <HiChevronRight className="group-hover:translate-x-1 transition-transform" />
            </motion.button>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="lg:hidden relative w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 shadow-lg z-50"
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <HiX size={24} className="text-red-600" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <HiMenuAlt3 size={24} className="text-gray-800" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />

            {/* Mobile Menu Panel */}
            <motion.div
              variants={mobileMenuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="lg:hidden fixed top-0 right-0 w-[85%] max-w-sm h-full bg-white z-40 shadow-2xl overflow-y-auto"
            >
              {/* Mobile Menu Links */}
              <div className="p-4 pt-20 space-y-2">
                {navLinks.map((link, index) => {
                  const isActive = isActiveLink(link.href);
                  const isHash = link.href.startsWith('/#');
                  const LinkComponent = isHash ? 'a' : Link;
                  const linkProps = isHash ? { href: link.href } : { to: link.href };
                  
                  return (
                    <motion.div
                      key={link.name}
                      variants={mobileItemVariants}
                      custom={index}
                    >
                      <LinkComponent
                        {...linkProps}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center justify-between p-4 rounded-2xl transition-all duration-300 group ${
                          isActive 
                            ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg shadow-red-500/30' 
                            : 'bg-white hover:bg-gray-100 text-gray-800 shadow-sm hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span className={`text-2xl ${isActive ? 'animate-bounce' : 'group-hover:scale-125 transition-transform'}`}>
                            <link.icon />
                          </span>
                          <div>
                            <span className="font-bold text-lg">{link.name}</span>
                            {isActive && (
                              <motion.p
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-xs text-red-100"
                              >
                                You are here
                              </motion.p>
                            )}
                          </div>
                        </div>
                        <motion.div
                          animate={isActive ? { x: [0, 5, 0] } : {}}
                          transition={{ repeat: Infinity, duration: 1 }}
                        >
                          <HiChevronRight className={`text-xl ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-red-500 group-hover:translate-x-1'} transition-all`} />
                        </motion.div>
                      </LinkComponent>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
